import { all, call, put, select, takeEvery } from "redux-saga/effects";
import { recordsActions } from "../actions";
import { ActionType, getType } from "typesafe-actions";
import {
  FrequencyRecord,
  FrequencyRecordRaw,
} from "../../../types/FrequencyRecord";
import createFrequencyRecord from "./utils/createFrequencyRecord";
import { getDisplayDateRange } from "../../productPage/selectors";
import dataAPI from "../../../services/dataServices";
import { AxiosResponse } from "axios";
import mapFrequencyRecordsDtoToFrequencyRecords from "./utils/mapFrequencyRecordsDtoToFrequencyRecords";
import {
  FrequencyRecordDto,
  TFrequencyRecord,
} from "../../../types/dto/FrequencyRecordDto";
import createRevisionRecord from "../revision/utils/createRevisionRecord";
import { RevisionRecord } from "../../../types/RevisionRecord";
import { DisplayDateRange } from "src/ducks/productPage/reducers";

const ODATA_4_PAGE = 10000;

export function* initializeFrequencyRecord(
  action: ActionType<typeof recordsActions.frequency.initializeFrequency>
) {
  try {
    const DateDisplayRange: DisplayDateRange = yield select(
      getDisplayDateRange
    );
    // Use UTC dates to avoid timezone issues
    const toDateString = new Date().toISOString().slice(0, 10);
    const fromDateUTC = new Date(Date.UTC(
      DateDisplayRange.to.getUTCFullYear() - 12,
      DateDisplayRange.to.getUTCMonth(),
      1
    ));
    const fromDateString = fromDateUTC.toISOString().slice(0, 10);
    const meta = {
      productID: action.payload,
      frequency: action.meta.frequency,
    };
    const fetchedFrequenceyRecords: Array<TFrequencyRecord> = yield call(
      getAllFrequencyRecords,
      { action, fromDateString, toDateString }
    );
    const rawRecords: FrequencyRecordRaw[] = yield mapFrequencyRecordsDtoToFrequencyRecords(
      fetchedFrequenceyRecords
    );
    const records: FrequencyRecord = yield call(
      createFrequencyRecord,
      rawRecords,
      action.meta.frequency
    );
    yield put(recordsActions.frequency.addFrequency(records, meta));
    const revision: RevisionRecord = yield call(
      createRevisionRecord,
      rawRecords,
      action.meta.frequency
    );
    yield put(recordsActions.revision.addRevision(revision, meta));
    if (action.meta.hasUsaMap) {
      const fetchedFrequenceyRecords: Array<TFrequencyRecord> = [];
      let hasNext = true;
      let iteration = 0;
      do {
        const skipFor = iteration !== 0 ? iteration * ODATA_4_PAGE : undefined;
        const usaRecordsDto: AxiosResponse<FrequencyRecordDto> = yield call(
          dataAPI.fetchUsaFrequencyRecord,
          {
            frequency: action.meta.frequency,
            from: fromDateString,
            productID: action.payload,
            to: toDateString,
            skipFor,
          }
        );
        usaRecordsDto.data.value.forEach((freqRec) => {
          fetchedFrequenceyRecords.push({ ...freqRec });
        });
        iteration++;
        hasNext = !!usaRecordsDto.data["@odata.nextLink"];
      } while (hasNext);
      const rawUsaRecords: FrequencyRecordRaw[] = yield mapFrequencyRecordsDtoToFrequencyRecords(
        fetchedFrequenceyRecords
      );
      const usaRecords: FrequencyRecord = yield call(
        createFrequencyRecord,
        rawUsaRecords,
        action.meta.frequency
      );
      yield put(recordsActions.frequency.addFrequency(usaRecords, meta));
      const usaRevision: RevisionRecord = yield call(
        createRevisionRecord,
        rawUsaRecords,
        action.meta.frequency
      );
      yield put(recordsActions.revision.addRevision(usaRevision, meta));
    }
  } catch (e) {
    console.error(e);
  }
}

export function* fetchFrequencyRecord(
  action: ActionType<typeof recordsActions.frequency.fetchFrequency>
) {
  try {
    const DateDisplayRange: DisplayDateRange = yield select(
      getDisplayDateRange
    );
    let fromDate = DateDisplayRange.from;
    let fromStr = "";
    let toDate = DateDisplayRange.to;
    let toStr = "";
    const toYear = DateDisplayRange.to.getUTCFullYear();
    const fromYear = DateDisplayRange.from.getUTCFullYear();
    if (toYear - fromYear < 12) {
      toStr = toDate.toISOString().slice(0, 10);
      const fromDateUTC = new Date(Date.UTC(toYear - 12, DateDisplayRange.to.getUTCMonth(), 1));
      fromStr = fromDateUTC.toISOString().slice(0, 10);
    } else {
      fromStr = fromDate.toISOString().slice(0, 10);
      toStr = toDate.toISOString().slice(0, 10);
    }
    const meta = {
      productID: action.payload,
      frequency: action.meta.frequency,
    };
    const fetchedFrequenceyRecords: Array<TFrequencyRecord> = yield call(
      getAllFrequencyRecords,
      {
        action,
        fromDateString: fromStr,
        toDateString: toStr,
      }
    );
    const rawRecords: FrequencyRecordRaw[] = yield mapFrequencyRecordsDtoToFrequencyRecords(
      fetchedFrequenceyRecords
    );
    const records: FrequencyRecord = yield call(
      createFrequencyRecord,
      rawRecords,
      action.meta.frequency
    );
    yield put(recordsActions.frequency.addFrequency(records, meta));
    const revision: RevisionRecord = yield call(
      createRevisionRecord,
      rawRecords,
      action.meta.frequency
    );
    yield put(recordsActions.revision.addRevision(revision, meta));
  } catch (e) {
    console.error(e);
  }
}

export function* initializeUnitsForUnitConversion(
  action: ActionType<
    typeof recordsActions.frequency.initializeUnitsForFrequency
  >
) {
  try {
    const fetchedFrequenceyRecords: Array<TFrequencyRecord> = [];
    let hasNext = true;
    let iteration = 0;
    do {
      const skipFor = iteration !== 0 ? iteration * ODATA_4_PAGE : undefined;
      const recordDto: AxiosResponse<FrequencyRecordDto> = yield call(
        dataAPI.fetchRecordsForUnitConversion,
        {
          frequency: action.meta,
          productID: action.payload,
          skipFor,
        }
      );
      recordDto.data.value.forEach((freqRec) => {
        fetchedFrequenceyRecords.push({ ...freqRec });
      });
      iteration++;
      hasNext = !!recordDto.data["@odata.nextLink"];
    } while (hasNext);
    const rawRecords: FrequencyRecordRaw[] = yield mapFrequencyRecordsDtoToFrequencyRecords(
      fetchedFrequenceyRecords
    );
    const records: FrequencyRecord = yield call(
      createFrequencyRecord,
      rawRecords,
      action.meta
    );
    yield put(
      recordsActions.frequency.addFrequency(records, {
        productID: action.payload,
        frequency: action.meta,
      })
    );
  } catch (e) {
    console.error(e);
  }
}

function* frequencyRecordWatcher() {
  yield all([
    takeEvery(
      getType(recordsActions.frequency.initializeFrequency),
      initializeFrequencyRecord
    ),
    takeEvery(
      getType(recordsActions.frequency.initializeUnitsForFrequency),
      initializeUnitsForUnitConversion
    ),
    takeEvery(
      getType(recordsActions.frequency.fetchFrequency),
      fetchFrequencyRecord
    ),
  ]);
}

export default frequencyRecordWatcher;

function* getAllFrequencyRecords({
  action,
  fromDateString,
  toDateString,
}: {
  action: ActionType<
    | typeof recordsActions.frequency.fetchFrequency
    | typeof recordsActions.frequency.initializeFrequency
  >;
  fromDateString: string;
  toDateString: string;
}) {
  const fetchedFrequenceyRecords: Array<TFrequencyRecord> = [];
  let hasNext = true;
  let iteration = 0;
  do {
    const skipFor = iteration !== 0 ? iteration * ODATA_4_PAGE : undefined;
    const recordsDto: AxiosResponse<FrequencyRecordDto> = yield call(
      dataAPI.fetchFrequencyRecord,
      {
        areaID: action.meta.areaID,
        frequency: action.meta.frequency,
        from: fromDateString,
        productID: action.payload,
        to: toDateString,
        skipFor,
      }
    );
    recordsDto.data.value.forEach((freqRec) => {
      fetchedFrequenceyRecords.push({ ...freqRec });
    });
    iteration++;
    hasNext = !!recordsDto.data["@odata.nextLink"];
  } while (hasNext);
  return fetchedFrequenceyRecords;
}
